import React from 'react'

import _ from 'underscore'

import {Button, Alert, IconButton} from '../UI/index.js'
import {formSpacing, FormRow, FormStack, Input, CurrencyInput, MetricInput, Select} from '../UI/forms/index.js'
import {Parcel} from '../../../server/functions/parcels/parcels.types.js'
import {outsideEUCustoms} from 'utils/EU.js'
import customsCurrencies, {getCustomsCurrency, getCurrencySymbol} from 'utils/customsCurrencies.js'
import t from 'src/helpers/Languages.js'

type ParcelCustomsProps = {
    parcel: Parcel
    onChange: (parcel: Parcel) => void
}

type ParcelCustomsState = {
    validation: {
        [key: string]: boolean
    }
    hsCodes: {
        [code: string]: string
    }
    error?: string
}

class ParcelCustoms extends React.Component<ParcelCustomsProps, ParcelCustomsState> {
    constructor(props: ParcelCustomsProps) {
        super(props)
        this.state = {
            validation: {},
            hsCodes: {}
        }
    }

    async componentDidMount() {
        try {
            const res = await fetch('/hs-codes', {
                headers: {
                    Accept: 'application/json'
                }
            })
            if (!res.ok) {
                throw new Error(await res.text())
            }

            const data = await res.json() as ParcelCustomsState['hsCodes']

            this.setState({hsCodes: data})
        } catch (error) {
            console.error(error)
            if (error instanceof Error) {
                this.setState({error: error.message})
            } else {
                this.setState({error: 'Failed to load HS codes'})
            }
        }
    }

    onChangeValue(key, event) {
        const {parcel, onChange} = this.props
        parcel.customs[key] = event.target.value

        if (key === 'typeItem') {
            if (parcel.customs.typeItem === 'Documents' || parcel.customs.typeItem === 'Gift') {
                parcel.customs.typeNumber = ''
                parcel.customs.number = ''
                parcel.customs.content.map((content) => {
                    content.hsCode = ''
                    content.countryOrigin = 'NL'
                })
            } else {
                parcel.customs.typeNumber = parcel.customs.typeNumber || 'invoice'
            }
        }

        onChange(parcel)
    }

    onChangeContentValue(index, key, event) {
        const {parcel, onChange} = this.props

        if (key === 'weight') {
            if (/^[0-9]{0,5}$/.test(event.target.value)) {
                parcel.customs.content[index][key] = parseInt(event.target.value) || 0
            }
        } else if (key === 'quantity') {
            parcel.customs.content[index][key] = parseInt(event.target.value) || 0
        } else {
            parcel.customs.content[index][key] = event.target.value
        }

        onChange(parcel)
    }

    onClickAddContent() {
        const {parcel, onChange} = this.props

        if (parcel.customs.content.length < 5) {
            parcel.customs.content.push({
                description: '',
                quantity: 1,
                weight: 0,
                value: '0,00',
                hsCode: '',
                countryOrigin: 'NL'
            })
        }

        onChange(parcel)
    }

    onClickRemoveContent(index) {
        const {parcel, onChange} = this.props

        if (parcel.customs.content.length > 1) {
            parcel.customs.content.splice(index, 1)
        }

        onChange(parcel)
    }

    validate() {
        const {parcel} = this.props

        let id = ''
        let valid = true
        const validation: ParcelCustomsState['validation'] = {}

        if ((outsideEUCustoms(parcel.address) || (parcel.address.country === 'IE' && parcel.carrier === 'DHL'))) {
            // PostNL requires a HS code for every type except documents
            const hsCodeRequired = parcel.customs.typeItem !== 'Documents' && (parcel.customs.typeItem !== 'Gift' || parcel.carrier?.includes('PostNL'))

            parcel.customs.content.map((content, index) => {
                if (hsCodeRequired && !content.hsCode) {
                    id = `hsCode${index}`
                    validation[`hsCode${index}`] = true
                    valid = false
                }

                if (parcel.customs.typeItem !== 'Documents' && parcel.customs.typeItem !== 'Gift') {
                    if (!content.value || content.value === '0,00') {
                        id = `value${index}`
                        validation[`value${index}`] = true
                        valid = false
                    }
                }

                if (!content.weight) {
                    id = `weight${index}`
                    validation[`weight${index}`] = true
                    valid = false
                }

                if (!content.description) {
                    id = `description${index}`
                    validation[`description${index}`] = true
                    valid = false
                }
            })

            if (parcel.customs.typeItem !== 'Documents' && parcel.customs.typeItem !== 'Gift' && !parcel.customs.number) {
                id = 'number'
                validation.number = true
                valid = false
            }
        }

        if (!valid) {
            const element = document.getElementById(id)
            if (element) {
                element.focus()
            }
        }

        this.setState({validation})
        return valid
    }

    render() {
        const {validation, hsCodes, error} = this.state
        const {parcel} = this.props
        const currency = getCustomsCurrency(parcel.customs)
        // A webshop can deliver a currency outside the list, show it so the user can see and change it
        const currencyOptions: string[] = (customsCurrencies as readonly string[]).includes(currency) ? [...customsCurrencies] : [...customsCurrencies, currency]

        return (
            <div style={{marginBottom: 12}}>
                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <FormRow>
                        <FormRow style={{flex: 1}}>
                            <Select
                                label='Type zending'
                                value={parcel.customs.typeItem}
                                onChange={this.onChangeValue.bind(this, 'typeItem')}
                            >
                                <option value='Commercial Goods'>Verkoop goederen</option>
                                <option value='Returned Goods'>Retour goederen</option>
                                <option value='Commercial Sample'>Handelsmonster</option>
                                <option value='Documents'>Documenten</option>
                                <option value='Gift'>Geschenk</option>
                            </Select>

                            <Select
                                style={{maxWidth: 100}}
                                label={t('customsCurrency')}
                                info={t('customsCurrencyInfo')}
                                value={currency}
                                onChange={this.onChangeValue.bind(this, 'currency')}
                                noSort
                            >
                                {currencyOptions.map((code) => {
                                    return (
                                        <option key={code} value={code}>{code}</option>
                                    )
                                })}
                            </Select>
                        </FormRow>
                        <FormRow style={{flex: 1}}>
                            {parcel.customs.typeItem !== 'Gift' && parcel.customs.typeItem !== 'Documents' &&
                                <>
                                    <Select
                                        label='Type nummer'
                                        value={parcel.customs.typeNumber}
                                        onChange={this.onChangeValue.bind(this, 'typeNumber')}
                                    >
                                        <option value='invoice'>Factuur</option>
                                        <option value='certificate'>Certificaat</option>
                                        <option value='license'>Licentie</option>
                                    </Select>

                                    <Input
                                        id='number'
                                        isInvalid={validation.number}
                                        label='Nummer'
                                        value={parcel.customs.number}
                                        onChange={this.onChangeValue.bind(this, 'number')}
                                    />
                                </>
                            }
                        </FormRow>
                    </FormRow>

                    {parcel.customs && parcel.customs.content.map((content, index) => {
                        return (
                            <FormRow key={`customsContent${index}`}>
                                <FormRow style={{flex: 1}}>
                                    <Input
                                        style={{maxWidth: 60}}
                                        type='number'
                                        label='Aantal'
                                        value={content.quantity}
                                        onChange={this.onChangeContentValue.bind(this, index, 'quantity')}
                                    />

                                    <Input
                                        id={`description${index}`}
                                        label='Omschrijving'
                                        isInvalid={validation[`description${index}`]}
                                        value={content.description}
                                        onChange={this.onChangeContentValue.bind(this, index, 'description')}
                                    />

                                    <MetricInput
                                        id={`weight${index}`}
                                        style={{maxWidth: 120}}
                                        label='Gewicht'
                                        isInvalid={validation[`weight${index}`]}
                                        max={999999999}
                                        divisionFactor={1000}
                                        value={content.weight}
                                        onChange={this.onChangeContentValue.bind(this, index, 'weight')}
                                        append='kg'
                                    />
                                </FormRow>

                                <FormRow style={{flex: 1}}>
                                    <CurrencyInput
                                        id={`value${index}`}
                                        style={{maxWidth: 120}}
                                        label='Waarde'
                                        prepend={getCurrencySymbol(currency)}
                                        value={content.value}
                                        onChange={this.onChangeContentValue.bind(this, index, 'value')}
                                        isInvalid={validation[`value${index}`]}
                                    />

                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flex: 1}}>
                                        <Select
                                            id={`hsCode${index}`}
                                            label='HS-code'
                                            onChange={this.onChangeContentValue.bind(this, index, 'hsCode')}
                                            value={content.hsCode}
                                            allowEmpty={true}
                                            allowFreeMode={true}
                                            isInvalid={validation[`hsCode${index}`]}
                                            maxOptions={100}
                                            listMaxWidth={600}
                                        >
                                            {_.keys(hsCodes).map((code) => {
                                                return (
                                                    <option key={code} value={code}>{`${code} ${hsCodes[code]}`}</option>
                                                )
                                            })}
                                        </Select>
                                    </div>

                                    <Input
                                        style={{maxWidth: 70}}
                                        label='Landcode'
                                        value={content.countryOrigin}
                                        onChange={this.onChangeContentValue.bind(this, index, 'countryOrigin')}
                                    />

                                    {parcel.customs.content.length > 1 &&
                                        <IconButton onClick={this.onClickRemoveContent.bind(this, index)}>
                                            <i className='mdi mdi-delete' />
                                        </IconButton>
                                    }
                                </FormRow>
                            </FormRow>
                        )
                    })}
                </FormStack>

                {parcel.customs.content.length < 5 &&
                    <Button
                        variant='outline-white'
                        icon='mdi mdi-plus'
                        label='Nieuw artikel'
                        onClick={this.onClickAddContent.bind(this)}
                    />
                }

                {error &&
                    <Alert style={{marginBottom: 6, marginTop: 8}} variant='error'>{error}</Alert>
                }
            </div>

        )
    }
}

export default ParcelCustoms
